// Static blog content, multilingual. Plain JSX-safe HTML strings.
// Slug-routed in BlogPostPage.

export const posts = [
  {
    slug: "comment-est-calculee-la-pension-au-luxembourg",
    date: "2026-02-01",
    keywords: "pension luxembourg, CNAP, calcul retraite luxembourg, formule pension",
    fr: {
      title: "Comment est calculée la pension au Luxembourg (CNAP) ?",
      excerpt: "La formule officielle simplifiée : majoration forfaitaire + majoration proportionnelle. Comprendre les deux composantes en 5 minutes.",
      body: `
<p>La pension du secteur privé luxembourgeois est gérée par la <strong>CNAP</strong> (Caisse Nationale d'Assurance Pension). Sa formule officielle additionne deux composantes :</p>
<h2>1. La majoration forfaitaire</h2>
<p>Elle dépend uniquement de la <em>durée de carrière</em> (40 ans pour une pension forfaitaire complète). Tous les assurés y ont droit, indépendamment du salaire perçu. Elle représente environ 558 €/mois pour une carrière complète en 2025.</p>
<h2>2. La majoration proportionnelle</h2>
<p>Elle est de l'ordre de <strong>1,85 % du total des salaires cotisés sur toute la carrière</strong>. Plus vous avez gagné, plus cette part est élevée. C'est la composante qui pèse le plus lourd pour les revenus moyens et supérieurs.</p>
<h2>Périodes complémentaires</h2>
<p>Les <strong>études supérieures</strong> (entre 18 et 27 ans) sont créditées comme <em>périodes complémentaires</em> : elles allongent la durée totale reconnue (utile pour la majoration forfaitaire et le seuil de pension minimum) sans ajouter au cumul des cotisations.</p>
<h2>Plafond</h2>
<p>La pension est plafonnée à environ <strong>10 277 €/mois</strong> en 2025.</p>
<p><a href="/" data-testid="link-to-calc">→ Estimez votre pension maintenant</a></p>
      `,
    },
    en: {
      title: "How is the Luxembourg pension calculated (CNAP)?",
      excerpt: "The official simplified formula: flat increment + proportional increment. Understand the two components in 5 minutes.",
      body: `
<p>Luxembourg's private-sector pension is administered by the <strong>CNAP</strong>. The official formula adds two parts:</p>
<h2>1. Flat increment</h2>
<p>Depends only on <em>career length</em> (40 years for a full flat pension). All insured persons get it regardless of salary. Roughly €558/month for a full career in 2025.</p>
<h2>2. Proportional increment</h2>
<p>Approximately <strong>1.85% of total career earnings</strong>. The higher your lifetime earnings, the larger this share. It dominates for mid-to-high earners.</p>
<h2>Complementary periods</h2>
<p><strong>Higher-education years</strong> (between 18 and 27) count as <em>complementary periods</em>: they extend total recognized duration (for the flat part and minimum pension threshold) without adding to lifetime earnings.</p>
<h2>Ceiling</h2>
<p>Maximum pension is capped around <strong>€10,277/month</strong> in 2025.</p>
<p><a href="/" data-testid="link-to-calc">→ Estimate your pension now</a></p>
      `,
    },
    de: {
      title: "Wie wird die Rente in Luxemburg (CNAP) berechnet?",
      excerpt: "Die offizielle vereinfachte Formel: pauschaler Zuschlag + proportionaler Zuschlag. Beide Bestandteile in 5 Minuten erklärt.",
      body: `
<p>Die luxemburgische Rente im Privatsektor wird von der <strong>CNAP</strong> verwaltet. Die offizielle Formel addiert zwei Bestandteile:</p>
<h2>1. Pauschaler Zuschlag</h2>
<p>Hängt nur von der <em>Karrieredauer</em> ab (40 Jahre für eine volle Pauschalrente). Alle Versicherten erhalten ihn unabhängig vom Gehalt. Etwa 558 €/Monat bei voller Karriere in 2025.</p>
<h2>2. Proportionaler Zuschlag</h2>
<p>Etwa <strong>1,85 % der gesamten beitragspflichtigen Karrierengelder</strong>. Je höher Ihr Lebenseinkommen, desto größer dieser Anteil.</p>
<h2>Ergänzungszeiten</h2>
<p><strong>Hochschuljahre</strong> (18–27) zählen als <em>Ergänzungszeiten</em>: sie verlängern die anerkannte Gesamtdauer, ohne das Beitragsvolumen zu erhöhen.</p>
<h2>Höchstgrenze</h2>
<p>Die Rente ist auf etwa <strong>10.277 €/Monat</strong> (2025) begrenzt.</p>
<p><a href="/" data-testid="link-to-calc">→ Jetzt Ihre Rente berechnen</a></p>
      `,
    },
  },
  {
    slug: "classe-impot-pension-luxembourg",
    date: "2026-02-05",
    keywords: "classe impot luxembourg, classe 2 retraite, splitting fiscal pension",
    fr: {
      title: "Classe d'impôt 1, 1a ou 2 : quel impact sur votre pension nette ?",
      excerpt: "La classe d'impôt peut faire varier votre pension nette de plusieurs centaines d'euros par mois. Comprendre le splitting fiscal.",
      body: `
<p>Au Luxembourg, votre <strong>classe d'impôt</strong> détermine comment vos revenus sont taxés. Pour un pensionné, la différence est significative :</p>
<ul>
  <li><strong>Classe 1</strong> — célibataire sans enfant : barème standard.</li>
  <li><strong>Classe 1a</strong> — veuf, parent isolé, divorcé après 41 ans : situation intermédiaire favorable.</li>
  <li><strong>Classe 2</strong> — marié ou pacsé : <em>splitting fiscal</em> — vos revenus sont divisés par deux pour l'application du barème, ce qui réduit fortement l'impôt sur les revenus élevés.</li>
</ul>
<p>Exemple : à pension brute égale de 5 500 €/mois, un pensionné en classe 1 paie environ <strong>1 084 €/mois</strong> d'impôt, contre seulement <strong>490 €/mois</strong> en classe 2. Soit près de <strong>600 € de plus chaque mois</strong> pour le couple marié.</p>
<h2>Crédits d'impôt</h2>
<p>Chaque enfant à charge donne droit à environ <strong>922,50 €/an</strong> de crédit (CIE). Un crédit pensionné supplémentaire (CIP) d'environ <strong>300 €/an</strong> s'applique aussi.</p>
<p><a href="/?tax_class=2&children=2" data-testid="link-prefill-class2">→ Tester le scénario classe 2 + 2 enfants</a></p>
      `,
    },
    en: {
      title: "Tax class 1, 1a or 2: what's the impact on your net pension?",
      excerpt: "Tax class can swing your net pension by hundreds of euros per month. Understanding tax splitting.",
      body: `
<p>In Luxembourg, your <strong>tax class</strong> determines how your income is taxed. For a pensioner, the difference is significant:</p>
<ul>
  <li><strong>Class 1</strong> — single, no children: standard brackets.</li>
  <li><strong>Class 1a</strong> — widow, single parent, divorced after 41: intermediate.</li>
  <li><strong>Class 2</strong> — married or partnered: <em>tax splitting</em> — income is divided by two for the bracket calculation, sharply lowering tax on higher incomes.</li>
</ul>
<p>Example: at the same gross pension of €5,500/mo, a class-1 pensioner pays about <strong>€1,084/mo</strong> in tax, vs only <strong>€490/mo</strong> in class 2. Nearly <strong>€600/mo more</strong> for the married couple.</p>
<h2>Tax credits</h2>
<p>Each dependent child gives about <strong>€922.50/year</strong> in credit (CIE). A pensioner credit (CIP) of about <strong>€300/year</strong> also applies.</p>
<p><a href="/?tax_class=2&children=2" data-testid="link-prefill-class2">→ Try the class-2 + 2 children scenario</a></p>
      `,
    },
    de: {
      title: "Steuerklasse 1, 1a oder 2: Auswirkung auf Ihre Nettorente?",
      excerpt: "Die Steuerklasse kann Ihre Nettorente um mehrere hundert Euro pro Monat verändern. Splitting verstehen.",
      body: `
<p>In Luxemburg bestimmt Ihre <strong>Steuerklasse</strong>, wie Ihr Einkommen besteuert wird. Für Rentner ist der Unterschied erheblich:</p>
<ul>
  <li><strong>Klasse 1</strong> — alleinstehend, kinderlos: Standardtarif.</li>
  <li><strong>Klasse 1a</strong> — verwitwet, alleinerziehend, geschieden nach 41: günstige Zwischenstellung.</li>
  <li><strong>Klasse 2</strong> — verheiratet oder verpartnert: <em>Ehegattensplitting</em> — Einkommen wird halbiert, was die Steuer bei höheren Einkommen stark senkt.</li>
</ul>
<p>Beispiel: Bei gleicher Bruttorente von 5.500 €/Mo zahlt ein Rentner in Klasse 1 etwa <strong>1.084 €/Mo</strong> Steuer, in Klasse 2 nur <strong>490 €/Mo</strong>. Fast <strong>600 €/Mo mehr</strong> für das verheiratete Paar.</p>
<p><a href="/?tax_class=2&children=2" data-testid="link-prefill-class2">→ Szenario Klasse 2 + 2 Kinder testen</a></p>
      `,
    },
  },
  {
    slug: "frontaliers-pension-luxembourg",
    date: "2026-02-10",
    keywords: "frontalier luxembourg pension, frontalier retraite, frontalier belge francais allemand",
    fr: {
      title: "Frontaliers : votre pension luxembourgeoise expliquée",
      excerpt: "Vous êtes frontalier français, belge ou allemand ? Voici comment votre carrière au Luxembourg génère une pension CNAP.",
      body: `
<p>Plus de <strong>220 000 frontaliers</strong> travaillent au Luxembourg depuis la France, la Belgique ou l'Allemagne. Chaque année cotisée au Luxembourg ouvre des droits à pension CNAP — payés à vie une fois à la retraite, même si vous vivez à l'étranger.</p>
<h2>Carrière mixte : règlement européen</h2>
<p>Le règlement européen <strong>883/2004</strong> coordonne les systèmes : chaque pays paiera sa quote-part proportionnelle aux années cotisées chez lui. Le Luxembourg verse sa pension directement sur votre compte, dans le pays de résidence.</p>
<h2>Combien après 25 ans au Luxembourg ?</h2>
<p>Pour un salaire moyen de 5 000 €/mois pendant 25 ans, la pension brute CNAP est d'environ <strong>2 750 €/mois</strong>, soit ~2 200 €/mois net (selon la classe d'impôt et le pays de résidence pour l'impôt final).</p>
<h2>Imposition</h2>
<p>La pension CNAP est imposée au Luxembourg <em>à la source</em>, puis exonérée (ou créditée) dans le pays de résidence selon la convention fiscale bilatérale.</p>
<p><a href="/?monthly_gross_salary=5000&current_age=40&career_start_age=30" data-testid="link-prefill-frontalier">→ Estimer comme frontalier (25 ans de carrière)</a></p>
      `,
    },
    en: {
      title: "Cross-border workers: your Luxembourg pension explained",
      excerpt: "French, Belgian or German cross-border worker? Here's how your Luxembourg career builds a CNAP pension.",
      body: `
<p>Over <strong>220,000 cross-border workers</strong> commute to Luxembourg from France, Belgium or Germany. Every year contributed in Luxembourg builds CNAP pension rights — paid for life at retirement, even abroad.</p>
<h2>Mixed career: EU coordination</h2>
<p>EU regulation <strong>883/2004</strong> coordinates the systems: each country pays its proportional share based on years worked there. Luxembourg pays its share directly to your bank account, in your country of residence.</p>
<h2>How much after 25 years in Luxembourg?</h2>
<p>For an average salary of €5,000/mo over 25 years, the gross CNAP pension is about <strong>€2,750/mo</strong>, around €2,200/mo net (depending on tax class and country-of-residence taxation).</p>
<p><a href="/?monthly_gross_salary=5000&current_age=40&career_start_age=30" data-testid="link-prefill-frontalier">→ Estimate as cross-border worker (25-year career)</a></p>
      `,
    },
    de: {
      title: "Grenzgänger: Ihre Luxemburger Rente erklärt",
      excerpt: "Französischer, belgischer oder deutscher Grenzgänger? So baut Ihre Karriere in Luxemburg eine CNAP-Rente auf.",
      body: `
<p>Über <strong>220.000 Grenzgänger</strong> pendeln nach Luxemburg aus Frankreich, Belgien oder Deutschland. Jedes in Luxemburg gezahlte Beitragsjahr begründet CNAP-Rentenansprüche — lebenslang gezahlt, auch im Ausland.</p>
<h2>EU-Koordinierung</h2>
<p>Die EU-Verordnung <strong>883/2004</strong> koordiniert die Systeme: Jedes Land zahlt seinen anteiligen Beitrag entsprechend der dortigen Beschäftigungsjahre.</p>
<p><a href="/?monthly_gross_salary=5000&current_age=40&career_start_age=30" data-testid="link-prefill-frontalier">→ Als Grenzgänger schätzen (25 Karrierejahre)</a></p>
      `,
    },
  },
];

export const findPost = (slug) => posts.find((p) => p.slug === slug);
